/**
 * Cryptography for Locket Album.
 *
 * - A random 256-bit master key is generated per album and lives only in the
 *   URL fragment (`#k=...`), so it is never sent to relays or Blossom servers.
 * - A per-album key is derived with HKDF-SHA256 from the master key, a public
 *   salt stored on the album event, and the album's d-tag. Sub-albums reuse the
 *   same master key and salt, but their own d-tag — yielding independent keys.
 * - Files are encrypted with AES-256-GCM using a fresh random 96-bit nonce per
 *   file. Nonces are stored alongside each file entry in the manifest.
 */

export function randomBytes(n: number): Uint8Array<ArrayBuffer> {
  const b = new Uint8Array(n);
  crypto.getRandomValues(b);
  return b;
}

export function bytesToB64u(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
}

export function b64uToBytes(s: string): Uint8Array<ArrayBuffer> {
  const b64 = s.replaceAll('-', '+').replaceAll('_', '/');
  const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
  const bin = atob(b64 + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function hexToBytes(hex: string): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

async function hkdf(master: Uint8Array, salt: Uint8Array, info: string): Promise<Uint8Array<ArrayBuffer>> {
  const keyMaterial = await crypto.subtle.importKey('raw', master as BufferSource, 'HKDF', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: salt as BufferSource, info: new TextEncoder().encode(info) },
    keyMaterial,
    256,
  );
  return new Uint8Array(bits);
}

/**
 * Derive the AES key for an album (or sub-album) from the master key.
 * The salt comes from the parent album event; `d` is the (sub-)album d-tag.
 */
export function deriveAlbumKey(master: Uint8Array, saltHex: string, d: string): Promise<Uint8Array<ArrayBuffer>> {
  return hkdf(master, hexToBytes(saltHex), `locket-album:v1:${d}`);
}

const keyCache = new Map<string, Promise<CryptoKey>>();

function importAesKey(raw: Uint8Array): Promise<CryptoKey> {
  const cacheKey = bytesToB64u(raw);
  let p = keyCache.get(cacheKey);
  if (!p) {
    p = crypto.subtle.importKey('raw', raw as BufferSource, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
    keyCache.set(cacheKey, p);
  }
  return p;
}

export async function encryptBytes(
  keyRaw: Uint8Array,
  data: Uint8Array,
): Promise<{ cipher: Uint8Array<ArrayBuffer>; nonce: string }> {
  const key = await importAesKey(keyRaw);
  const nonce = randomBytes(12);
  const buf = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, key, data as BufferSource);
  return { cipher: new Uint8Array(buf), nonce: bytesToB64u(nonce) };
}

export async function decryptBytes(
  keyRaw: Uint8Array,
  nonce: string,
  cipher: Uint8Array,
): Promise<Uint8Array<ArrayBuffer>> {
  const key = await importAesKey(keyRaw);
  const buf = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: b64uToBytes(nonce) }, key, cipher as BufferSource);
  return new Uint8Array(buf);
}

export async function sha256Hex(data: Uint8Array): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', data as BufferSource);
  return bytesToHex(new Uint8Array(buf));
}

/** Generate a new master key and return it base64url-encoded (for the URL fragment). */
export function generateMasterKeyB64u(): string {
  return bytesToB64u(randomBytes(32));
}

/** Generate a random album id (d-tag). */
export function generateAlbumId(): string {
  return bytesToHex(randomBytes(12));
}

/** Generate the public per-album salt (hex). */
export function generateSaltHex(): string {
  return bytesToHex(randomBytes(16));
}
